import { useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Footer } from '@/components/layout/Footer';

interface LicenseEntry {
  name: string;
  version?: string;
  license?: string;
  licenseText?: string;
  repository?: string;
  author?: string;
}

export function LicensesPage() {
  const [licenses, setLicenses] = useState<LicenseEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<LicenseEntry | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetch('/third-party-licenses.json')
      .then((res) => {
        if (!res.ok) throw new Error('ライセンスファイルが見つかりません');
        return res.json() as Promise<LicenseEntry[]>;
      })
      .then((data) => {
        const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name));
        setLicenses(sorted);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'ライセンス情報の読み込みに失敗しました');
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = licenses.filter(
    (l) =>
      l.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (l.license ?? '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-lg">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          {selected ? (
            <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              一覧に戻る
            </Button>
          ) : (
            <Button variant="ghost" size="sm" asChild>
              <Link to="/">
                <ArrowLeft className="h-4 w-4 mr-2" />
                戻る
              </Link>
            </Button>
          )}
          <h1 className="text-lg font-semibold">
            {selected ? selected.name : 'オープンソースライセンス'}
          </h1>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        {loading && <div className="text-center text-muted-foreground py-12">読み込み中...</div>}

        {error && (
          <div className="text-center text-muted-foreground py-12">
            <p>{error}</p>
            <p className="text-sm mt-2">ライセンス情報はビルド済み版で利用可能です。</p>
          </div>
        )}

        {!loading && !error && selected && (
          <div>
            <div className="mb-6 space-y-2 text-sm">
              {selected.version && (
                <p>
                  <span className="text-muted-foreground">バージョン: </span>
                  {selected.version}
                </p>
              )}
              {selected.license && (
                <p>
                  <span className="text-muted-foreground">ライセンス: </span>
                  <span className="font-mono">{selected.license}</span>
                </p>
              )}
              {selected.repository && (
                <p>
                  <span className="text-muted-foreground">リポジトリ: </span>
                  <span className="font-mono text-xs break-all">{selected.repository}</span>
                </p>
              )}
              {selected.author && (
                <p>
                  <span className="text-muted-foreground">作者: </span>
                  {selected.author}
                </p>
              )}
            </div>
            {selected.licenseText ? (
              <pre className="text-xs font-mono whitespace-pre-wrap text-muted-foreground bg-muted rounded-lg p-6 leading-relaxed overflow-x-auto">
                {selected.licenseText}
              </pre>
            ) : (
              <p className="text-sm text-muted-foreground">ライセンステキストは利用できません。</p>
            )}
          </div>
        )}

        {!loading && !error && !selected && (
          <div>
            <div className="mb-4">
              <h2 className="text-2xl font-bold mb-2">オープンソースライセンス</h2>
              <p className="text-sm text-muted-foreground">
                本サービスは以下のオープンソースソフトウェアを使用しています。
              </p>
            </div>
            <div className="mb-4">
              <input
                type="text"
                placeholder="パッケージを検索..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full max-w-sm border border-border rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <p className="text-xs text-muted-foreground mt-2">{filtered.length} 件のパッケージ</p>
            </div>
            <div className="border border-border rounded-lg divide-y divide-border">
              {filtered.map((entry) => (
                <button
                  key={entry.name}
                  onClick={() => setSelected(entry)}
                  className="w-full text-left px-4 py-3 hover:bg-muted transition-colors flex items-center justify-between gap-4 focus:outline-none focus:bg-muted"
                >
                  <div className="min-w-0">
                    <span className="font-mono text-sm">{entry.name}</span>
                    {entry.version && (
                      <span className="text-xs text-muted-foreground ml-2">{entry.version}</span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {entry.license ?? '不明'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default LicensesPage;
